/**
 * Anomaly Activity Panel
 * ----------------------
 * Honest, dual-layer view of anomaly activity for the selected entity.
 *
 * Pattern of Life runs anomaly detection in two complementary layers:
 *
 *   Layer 1 — ML Autoencoder (daily): a per-entity LSTM autoencoder reconstructs
 *             each day's embedding vector. Days whose reconstruction error exceeds
 *             the 95th percentile baseline are flagged as a single "anomalous day".
 *             Strong signal but coarse — one bit per day, only fires when an entire
 *             day looks weird.
 *
 *   Layer 2 — Statistical detectors (event-level): time_shift, dwell_duration_anomaly,
 *             lapse_break, route_deviation, new_relationship, unusual_location. These
 *             fire per-event using median+MAD baselines, so multiple events can land
 *             on the same day, and many days can be touched without ever tripping
 *             Layer 1.
 *
 * The previous version of this panel showed *only* Layer 1 stats, which made it look
 * like "1 anomalous day" even when Layer 2 fired 33 events across several days. This
 * version takes an optional `detectorEvents` prop and renders a combined picture so
 * the panel never misleads — the badge and KPI strip always reflect the total view,
 * with a "Detection Layers" section that splits ML vs Detectors transparently.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  LinearProgress,
  Chip,
  Stack,
  Divider,
  Alert,
  Collapse,
  alpha,
} from '@mui/material';
import {
  Warning as AnomalyIcon,
  Psychology as AIIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  CalendarMonth as CalendarIcon,
  CheckCircle as NormalIcon,
  AutoAwesome as ModelIcon,
  RuleFolder as DetectorIcon,
} from '@mui/icons-material';
import EmptyState from '@signals/app-shell/components/common/EmptyState';
import { useTheme } from '@mui/material/styles';
import polService, {
  AnomalySummary,
} from '../../services/polService';

/** Minimal shape needed to add Layer-2 events into the combined view. */
export interface DetectorEventInput {
  /** ISO datetime, e.g. "2026-05-06T20:00:00Z". */
  date: string;
  anomaly_type?: string;
  severity?: string;
  deviation_score?: number;
}

interface AnomalyDetectionPanelProps {
  selectedEntityId: string | null;
  courseId: string;
  onDaySelect?: (date: string) => void;
  compact?: boolean;
  daysToShow?: number;
  /**
   * Statistical-detector events (Layer 2). When provided, the panel merges
   * them with the ML summary to render an accurate combined view. When
   * omitted, the panel falls back to ML-only and labels itself honestly.
   */
  detectorEvents?: DetectorEventInput[];
}

/**
 * Normalize any ISO date/datetime to a 'YYYY-MM-DD' local-day key so that
 * Layer-1 day rows and Layer-2 event timestamps land in the same buckets.
 */
const toDayKey = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const t = new Date(iso);
  if (isNaN(t.getTime())) return '';
  const y = t.getFullYear();
  const m = String(t.getMonth() + 1).padStart(2, '0');
  const d = String(t.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const AnomalyDetectionPanel: React.FC<AnomalyDetectionPanelProps> = ({
  selectedEntityId,
  courseId,
  onDaySelect,
  compact = false,
  daysToShow = 30,
  // Explicit cast preserves the element type — under strict generics a bare
  // `= []` default infers as `never[]` and breaks downstream `.date`/`.severity`
  // access in the combined-aggregation memo below.
  detectorEvents = [] as DetectorEventInput[],
}) => {
  const theme = useTheme();

  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<AnomalySummary | null>(null);
  const [expanded, setExpanded] = useState(true);

  // Fetch ML autoencoder summary (Layer 1).
  const fetchAnomalySummary = useCallback(async () => {
    if (!selectedEntityId || !courseId) {
      setSummary(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await polService.getAnomalySummary(selectedEntityId, courseId, daysToShow);
      setSummary(data);
    } catch (err: any) {
      if (err.response?.status === 501) {
        setError('Anomaly detection service not available.');
      } else {
        setError(err.message || 'Failed to load anomaly data');
      }
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [selectedEntityId, courseId, daysToShow]);

  useEffect(() => {
    fetchAnomalySummary();
  }, [fetchAnomalySummary]);

  // Color ramp shared by both layers — single source of truth for severity hues.
  const getScoreColor = useCallback(
    (score: number): string => {
      if (score >= 0.8) return theme.palette.error.main;
      if (score >= 0.6) return theme.palette.warning.main;
      if (score >= 0.4) return theme.palette.info.main;
      return theme.palette.success.main;
    },
    [theme]
  );

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  /**
   * Combined-layer aggregation: produces every metric the UI shows in one pass.
   * Computed even when Layer 2 events are empty so the panel renders consistently.
   *
   * Output shape:
   *   - layer1Days:        # days flagged by ML autoencoder
   *   - layer2Events:      total Layer 2 events (event count, not day count)
   *   - layer2DaysTouched: distinct days with >= 1 Layer 2 event
   *   - combinedDays:      distinct days with EITHER an ML flag OR a Layer 2 event
   *   - totalEvents:       layer1Days + layer2Events (treats each ML day as one event)
   *   - daysAnalyzed:      from the ML summary
   *   - perDay:            Map<dayKey, { mlScore, eventCount, peakSeverityRank }>
   *   - topDays:           top 5 days sorted by combined activity (eventCount + ML)
   */
  const combined = useMemo(() => {
    const perDay = new Map<string, {
      mlScore: number;
      eventCount: number;
      peakSeverityRank: number;
    }>();

    // Severity ranking so we can color days by their *worst* event.
    const sevRank = (sev?: string): number => {
      switch ((sev || '').toLowerCase()) {
        case 'critical': return 4;
        case 'high': return 3;
        case 'medium': return 2;
        case 'low': return 1;
        default: return 0;
      }
    };

    // Seed from Layer 1 (ML autoencoder days).
    (summary?.recent_anomalies || []).forEach((a) => {
      const key = toDayKey(a.date);
      if (!key) return;
      const cur = perDay.get(key) || { mlScore: 0, eventCount: 0, peakSeverityRank: 0 };
      cur.mlScore = Math.max(cur.mlScore, a.anomaly_score || 0);
      perDay.set(key, cur);
    });

    // Fold in Layer 2 (detector events).
    detectorEvents.forEach((e) => {
      const key = toDayKey(e.date);
      if (!key) return;
      const cur = perDay.get(key) || { mlScore: 0, eventCount: 0, peakSeverityRank: 0 };
      cur.eventCount += 1;
      cur.peakSeverityRank = Math.max(cur.peakSeverityRank, sevRank(e.severity));
      perDay.set(key, cur);
    });

    const layer1Days = summary?.anomalous_days || 0;
    const layer2Events = detectorEvents.length;
    const layer2DaysTouched = new Set(
      detectorEvents.map((e) => toDayKey(e.date)).filter(Boolean)
    ).size;
    const combinedDays = perDay.size;
    const totalEvents = layer1Days + layer2Events;
    const daysAnalyzed = summary?.total_days_analyzed || daysToShow;

    // Rank days by combined activity for the "Recent Activity" list.
    const topDays = Array.from(perDay.entries())
      .map(([day, v]) => ({ day, ...v }))
      .sort((a, b) => {
        // Primary: event count, Secondary: ML score, Tertiary: severity rank.
        if (b.eventCount !== a.eventCount) return b.eventCount - a.eventCount;
        if (b.mlScore !== a.mlScore) return b.mlScore - a.mlScore;
        return b.peakSeverityRank - a.peakSeverityRank;
      })
      .slice(0, 5);

    return {
      layer1Days,
      layer2Events,
      layer2DaysTouched,
      combinedDays,
      totalEvents,
      daysAnalyzed,
      perDay,
      topDays,
    };
  }, [summary, detectorEvents, daysToShow]);

  // Severity color for a day-summary row (uses peak severity if Layer 2 events
  // are present, otherwise falls back to ML score).
  const dayRowColor = useCallback(
    (row: { mlScore: number; eventCount: number; peakSeverityRank: number }): string => {
      if (row.peakSeverityRank > 0) {
        // critical/high/medium/low → red/amber/blue/green
        if (row.peakSeverityRank >= 4) return theme.palette.error.main;
        if (row.peakSeverityRank >= 3) return theme.palette.warning.main;
        if (row.peakSeverityRank >= 2) return theme.palette.info.main;
        return theme.palette.success.main;
      }
      return getScoreColor(row.mlScore);
    },
    [theme, getScoreColor]
  );

  // Empty state
  if (!selectedEntityId) {
    return (
      <Paper
        sx={{
          p: 2,
          bgcolor: alpha(theme.palette.background.paper, 0.8),
          borderRadius: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ opacity: 0.7 }}>
          <AnomalyIcon fontSize="small" />
          <Typography variant="body2" color="text.secondary">
            Select an entity to view anomaly activity
          </Typography>
        </Stack>
      </Paper>
    );
  }

  const hasAnyActivity = combined.totalEvents > 0;

  return (
    <Paper
      sx={{
        bgcolor: alpha(theme.palette.background.paper, 0.95),
        borderRadius: 2,
        overflow: 'hidden',
        border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: compact ? 1.5 : 2,
          bgcolor: alpha(theme.palette.warning.main, 0.1),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <AIIcon sx={{ color: theme.palette.warning.main }} />
          <Stack direction="column" spacing={0}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ lineHeight: 1.2 }}>
              Anomaly Activity
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', lineHeight: 1.1 }}>
              ML model + statistical detectors · last {combined.daysAnalyzed} days
            </Typography>
          </Stack>
          {hasAnyActivity && (
            <Chip
              label={`${combined.totalEvents} ${combined.totalEvents === 1 ? 'event' : 'events'}`}
              size="small"
              color="warning"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
        </Stack>

        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={fetchAnomalySummary} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={() => setExpanded(!expanded)}>
            {expanded ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        </Stack>
      </Box>

      <Collapse in={expanded}>
        {/* Loading State */}
        {loading && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Analyzing behavioral patterns...
            </Typography>
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="warning" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && summary && (
          <>
            {/* All-zero edge case: be explicit about why */}
            {combined.totalEvents === 0 && summary.total_days_analyzed === 0 && (
              <EmptyState
                dense
                sx={{ m: 2, mb: 1 }}
                icon={<AIIcon />}
                title="No anomaly data yet"
                description="Models are trained automatically by the daily embedding pipeline. If this entity was recently added, data will appear after the next pipeline run."
              />
            )}

            <Box sx={{ p: 2 }}>
              {/* ─── Top KPI strip: combined view across both layers ─────────── */}
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Tooltip title="Total anomaly events fired across both layers in this window. ML autoencoder days count as one event each; each statistical detector hit counts individually." placement="top" arrow>
                  <Box sx={{ flex: 1, textAlign: 'center', cursor: 'help' }}>
                    <Typography
                      variant="h4"
                      fontWeight={700}
                      color={hasAnyActivity ? theme.palette.warning.main : 'text.secondary'}
                    >
                      {combined.totalEvents}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total Events
                    </Typography>
                  </Box>
                </Tooltip>
                <Divider orientation="vertical" flexItem />
                <Tooltip title="Number of distinct days with at least one anomaly event from either layer." placement="top" arrow>
                  <Box sx={{ flex: 1, textAlign: 'center', cursor: 'help' }}>
                    <Typography variant="h4" fontWeight={700}>
                      {combined.combinedDays}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Active Days
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      display="block"
                      sx={{ fontSize: '0.6rem', lineHeight: 1, mt: 0.25 }}
                    >
                      of {combined.daysAnalyzed} analyzed
                    </Typography>
                  </Box>
                </Tooltip>
                <Divider orientation="vertical" flexItem />
                <Tooltip title="Peak ML autoencoder reconstruction-error score over the window. Captures how unusual the worst day looks to the personal AI model." placement="top" arrow>
                  <Box sx={{ flex: 1, textAlign: 'center', cursor: 'help' }}>
                    <Typography
                      variant="h4"
                      fontWeight={700}
                      color={getScoreColor(summary.max_anomaly_score)}
                    >
                      {(summary.max_anomaly_score * 100).toFixed(0)}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Peak ML Score
                    </Typography>
                  </Box>
                </Tooltip>
              </Stack>

              {/* ─── Daily activity heat strip (one cell per day) ─────────────── */}
              {/* Renders the analysis window as a horizontal strip. Each day's
                  fill intensity reflects combined activity for that day, giving
                  an instant temporal pattern. */}
              {combined.daysAnalyzed > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Daily Activity
                    </Typography>
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem' }}>
                      newest →
                    </Typography>
                  </Stack>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${combined.daysAnalyzed}, 1fr)`,
                      gap: '2px',
                      height: 18,
                    }}
                  >
                    {Array.from({ length: combined.daysAnalyzed }).map((_, i) => {
                      // Build the day key going backwards from today.
                      const d = new Date();
                      d.setDate(d.getDate() - (combined.daysAnalyzed - 1 - i));
                      const key = toDayKey(d.toISOString());
                      const row = combined.perDay.get(key);
                      const intensity = row
                        ? Math.min(1, (row.eventCount || 0) / 4 + (row.mlScore || 0) * 0.6)
                        : 0;
                      const color = row ? dayRowColor(row) : theme.palette.divider;
                      return (
                        <Tooltip
                          key={key + i}
                          title={
                            row
                              ? `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · ${row.eventCount} event${row.eventCount === 1 ? '' : 's'}${row.mlScore > 0 ? ` · ML ${(row.mlScore * 100).toFixed(0)}%` : ''}`
                              : `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · no activity`
                          }
                          placement="top"
                          arrow
                        >
                          <Box
                            onClick={() => row && onDaySelect?.(key)}
                            sx={{
                              borderRadius: 0.5,
                              bgcolor: row
                                ? alpha(color, Math.max(0.25, intensity))
                                : alpha(theme.palette.divider, 0.3),
                              border: row ? `1px solid ${alpha(color, 0.6)}` : '1px solid transparent',
                              cursor: row && onDaySelect ? 'pointer' : 'default',
                              transition: 'transform 0.15s',
                              '&:hover': row ? { transform: 'scaleY(1.2)' } : {},
                            }}
                          />
                        </Tooltip>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* ─── Detection-layer split ────────────────────────────────────── */}
              {/* Two compact cards making it obvious that "1 anomalous day" was
                  the ML view only, and that the detectors contribute their own
                  view independently. */}
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Tooltip
                  title="LSTM autoencoder per-entity. Flags whole days whose embedding-space reconstruction error exceeds the 95th percentile. Coarse but high-signal."
                  placement="top"
                  arrow
                >
                  <Box
                    sx={{
                      flex: 1,
                      p: 1,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: alpha(theme.palette.info.main, 0.4),
                      bgcolor: alpha(theme.palette.info.main, 0.05),
                      cursor: 'help',
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                      <ModelIcon sx={{ fontSize: 14, color: 'info.main' }} />
                      <Typography
                        sx={{
                          fontSize: '0.6rem',
                          textTransform: 'uppercase',
                          letterSpacing: 0.4,
                          fontWeight: 700,
                          color: 'info.main',
                        }}
                      >
                        AI Model (Daily)
                      </Typography>
                    </Stack>
                    <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1 }}>
                      {combined.layer1Days}
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                        / {combined.daysAnalyzed} {combined.daysAnalyzed === 1 ? 'day' : 'days'}
                      </Typography>
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                      {summary.anomaly_rate.toFixed(1)}% flag rate
                    </Typography>
                  </Box>
                </Tooltip>
                <Tooltip
                  title="Per-event statistical detectors using median+MAD baselines (time_shift, dwell duration, lapse_break, route_deviation, new_relationship, unusual_location). Fine-grained — many can fire on the same day."
                  placement="top"
                  arrow
                >
                  <Box
                    sx={{
                      flex: 1,
                      p: 1,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: alpha(theme.palette.warning.main, 0.4),
                      bgcolor: alpha(theme.palette.warning.main, 0.05),
                      cursor: 'help',
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                      <DetectorIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                      <Typography
                        sx={{
                          fontSize: '0.6rem',
                          textTransform: 'uppercase',
                          letterSpacing: 0.4,
                          fontWeight: 700,
                          color: 'warning.main',
                        }}
                      >
                        Detectors (Events)
                      </Typography>
                    </Stack>
                    <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1 }}>
                      {combined.layer2Events}
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                        on {combined.layer2DaysTouched} {combined.layer2DaysTouched === 1 ? 'day' : 'days'}
                      </Typography>
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                      avg {combined.layer2DaysTouched > 0
                        ? (combined.layer2Events / combined.layer2DaysTouched).toFixed(1)
                        : '0'} events / active day
                    </Typography>
                  </Box>
                </Tooltip>
              </Stack>

              {/* ─── Avg / Peak ML score bars (kept — useful Layer-1 detail) ─── */}
              <Box sx={{ mb: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Avg ML Score
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {(summary.avg_anomaly_score * 100).toFixed(1)}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={summary.avg_anomaly_score * 100}
                  sx={{
                    height: 6,
                    borderRadius: 1,
                    bgcolor: alpha(theme.palette.grey[500], 0.2),
                    '& .MuiLinearProgress-bar': {
                      bgcolor: getScoreColor(summary.avg_anomaly_score),
                      borderRadius: 1,
                    },
                  }}
                />
              </Box>
            </Box>

            {/* ─── Recent Activity (top 5 days by combined activity) ─────────── */}
            {combined.topDays.length > 0 && (
              <>
                <Divider />
                <Box sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Most Active Days
                  </Typography>
                  <Stack spacing={0.75}>
                    {combined.topDays.map((row) => {
                      const color = dayRowColor(row);
                      const layerTags: string[] = [];
                      if (row.mlScore > 0) layerTags.push(`ML ${(row.mlScore * 100).toFixed(0)}%`);
                      if (row.eventCount > 0) layerTags.push(`${row.eventCount} event${row.eventCount === 1 ? '' : 's'}`);
                      return (
                        <Box
                          key={row.day}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 1,
                            borderRadius: 1,
                            bgcolor: alpha(color, 0.1),
                            cursor: onDaySelect ? 'pointer' : 'default',
                            '&:hover': onDaySelect ? { bgcolor: alpha(color, 0.2) } : {},
                          }}
                          onClick={() => onDaySelect?.(row.day)}
                        >
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <CalendarIcon fontSize="small" sx={{ color }} />
                            <Stack direction="column" spacing={0}>
                              <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
                                {formatDate(row.day)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', lineHeight: 1 }}>
                                {layerTags.join(' · ')}
                              </Typography>
                            </Stack>
                          </Stack>
                          <Stack direction="row" spacing={0.5}>
                            {row.eventCount > 0 && (
                              <Chip
                                label={row.eventCount}
                                size="small"
                                sx={{
                                  height: 20, fontSize: '0.7rem',
                                  bgcolor: alpha(theme.palette.warning.main, 0.2),
                                  color: theme.palette.warning.main,
                                  fontWeight: 700,
                                }}
                              />
                            )}
                            {row.mlScore > 0 && (
                              <Chip
                                label={`${(row.mlScore * 100).toFixed(0)}%`}
                                size="small"
                                sx={{
                                  height: 20, fontSize: '0.7rem',
                                  bgcolor: alpha(theme.palette.info.main, 0.2),
                                  color: theme.palette.info.main,
                                  fontWeight: 700,
                                }}
                              />
                            )}
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              </>
            )}

            {/* No Data Message */}
            {summary.total_days_analyzed === 0 && combined.layer2Events === 0 && (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No anomaly data for this entity yet. Models are trained automatically by the
                  daily embedding pipeline (runs at 4:00 AM UTC).
                </Typography>
              </Box>
            )}

            {/* All-clear message — only when we genuinely have nothing across both layers */}
            {summary.total_days_analyzed > 0 && combined.totalEvents === 0 && (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <NormalIcon sx={{ fontSize: 40, color: theme.palette.success.main, mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  No anomalies detected in the last {daysToShow} days.
                  <br />
                  Behavior appears consistent with established patterns.
                </Typography>
              </Box>
            )}
          </>
        )}

        {/* No Data State */}
        {!loading && !error && !summary && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No anomaly data available for this entity. The embedding pipeline runs daily and will
              generate anomaly scores automatically.
            </Typography>
          </Box>
        )}
      </Collapse>
    </Paper>
  );
};

export default AnomalyDetectionPanel;
